import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from './supabaseClient';
import imageCompression from 'browser-image-compression';
import { fuzzySearchPlants } from './plantSearchUtils';
import { slugify } from './slugUtils';

const SALE_TYPES = ['Potted', 'Cutting', 'Plug/Starter', 'Tissue Culture (TC)'];
const POT_SIZES = ['2', '3', '4', '6', '8', '10', '12'];
const STATUSES = ['available', 'rehab', 'sold', 'died'];

const emptyPlant = () => ({
  common_name: '', genus: '', scientific_name: '', sale_type: '', sale_size: '',
  cost_price: '', price: '', quantity: '', notes: '',
  variegation_level: '', variegation_pattern: '',
  status: 'available', photo: null, photoPreview: null, image_url: '',
  is_public: false,
  image_url_2: '', image_url_3: '',
  shipping_fee: '', estimated_shipping: '', returns_accepted: false,
});

const formatPrice = (val) => {
  if (!val) return '$0.00';
  const num = parseFloat(String(val).replace(/[^0-9.]/g, ''));
  return isNaN(num) ? '$0.00' : `$${num.toFixed(2)}`;
};

const parsePrice = (val) => {
  if (!val) return 0;
  return parseFloat(String(val).replace(/[^0-9.]/g, '')) || 0;
};

const SellerDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [seller, setSeller] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('all');
  const [filterType, setFilterType] = useState('');
  const [filterSize, setFilterSize] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [activeTab, setActiveTab] = useState('inventory');
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replySaving, setReplySaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyPlant());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [plantRecords, setPlantRecords] = useState([]);
  const [variegatedMap, setVariegatedMap] = useState({});
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionIndex, setSuggestionIndex] = useState(-1);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [plantPriceData, setPlantPriceData] = useState([]);
  const [priceLoading, setPriceLoading] = useState(false);
  const [profileForm, setProfileForm] = useState({
    business_name: '', bio: '', tagline: '', location_city: '', location_state: '',
    etsy_url: '', palmstreet_url: '', ebay_url: '', facebook_url: '',
    instagram_url: '', website_url: '', profile_photo_url: '',
  });
  const [soldModal, setSoldModal] = useState(null);
  const [soldForm, setSoldForm] = useState({ price: '', source: 'Palmstreet', date: '' });
  const [mobile, setMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate('/seller/login'); return; }
      setUser(session.user);

      supabase.auth.onAuthStateChange((event, session) => {
        if (event === 'SIGNED_OUT') navigate('/seller/login');
        if (session) setUser(session.user);
      });

      let { data: sellerData } = await supabase.from('sellers').select('*').eq('id', session.user.id).single();
      if (!sellerData) {
        const { data: newSeller } = await supabase.from('sellers').upsert({
          id: session.user.id,
          business_name: session.user.email.split('@')[0],
        }, { onConflict: 'id' }).select().single();
        sellerData = newSeller;
      }
      if (sellerData) {
        setSeller(sellerData);
        setProfileForm({
          business_name: sellerData.business_name || '',
          bio: sellerData.bio || '',
          tagline: sellerData.tagline || '',
          location_city: sellerData.location_city || '',
          location_state: sellerData.location_state || '',
          etsy_url: sellerData.etsy_url || '',
          palmstreet_url: sellerData.palmstreet_url || '',
          ebay_url: sellerData.ebay_url || '',
          facebook_url: sellerData.facebook_url || '',
          instagram_url: sellerData.instagram_url || '',
          website_url: sellerData.website_url || '',
          profile_photo_url: sellerData.profile_photo_url || '',
        });
      }

      const { data: inv } = await supabase.from('inventory').select('*').eq('seller_id', session.user.id).order('date_listed', { ascending: false });
      setInventory(inv || []);

      let sFrom = 0;
      let allRows = [];
      while (true) {
        const { data: pageData } = await supabase.from('plants').select('common_name, scientific_name, genus').eq('status', 'approved').range(sFrom, sFrom + 999);
        if (!pageData || pageData.length === 0) break;
        allRows = allRows.concat(pageData);
        if (pageData.length < 1000) break;
        sFrom += 1000;
      }
      setPlantRecords(allRows);

      const { data: photoData } = await supabase.from('plant_header_photos').select('common_name, is_variegated');
      if (photoData) {
        const map = {};
        photoData.forEach(p => { if (p.common_name) map[p.common_name.toLowerCase()] = !!p.is_variegated; });
        setVariegatedMap(map);
      }

      setLoading(false);
    };
    init();
  }, [navigate]);

  const isVariegated = (name) => !!variegatedMap[name?.toLowerCase()];

  const fetchPlantPrices = async (name) => {
    if (!name) { setPlantPriceData([]); return; }
    setPriceLoading(true);
    const { data } = await supabase
      .from('plants')
      .select('sold_price, sale_type, sale_size')
      .eq('status', 'approved')
      .ilike('common_name', name.trim());
    setPlantPriceData(data || []);
    setPriceLoading(false);
  };

  const getPriceRange = (saleType, saleSize) => {
    if (!plantPriceData.length || !saleType) return null;
    let filtered = plantPriceData.filter(p => p.sale_type === saleType);
    if (saleType === 'Potted' && saleSize) filtered = filtered.filter(p => p.sale_size === saleSize);
    if (saleType === 'Potted' && !saleSize) return null; // wait for size selection
    const prices = filtered
      .map(p => parseFloat(String(p.sold_price || '').replace(/[^0-9.]/g, '')))
      .filter(n => !isNaN(n) && n > 0);
    if (prices.length === 0) return null;
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const avg = prices.reduce((a, b) => a + b, 0) / prices.length;
    return { min, max, avg: Math.round(avg), count: prices.length };
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/seller/login');
  };

  const handleFormChange = (e) => {
    const updated = { ...form, [e.target.name]: e.target.value };
    if (e.target.name === 'common_name') {
      const val = e.target.value;
      if (val.length > 1) {
        const matches = fuzzySearchPlants(val, plantRecords, 8);
        setSuggestions(matches.map(m => m.common_name));
      } else { setSuggestions([]); }
      if (!isVariegated(e.target.value)) {
        updated.variegation_level = '';
        updated.variegation_pattern = '';
      }
    }
    setForm(updated);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) setForm({ ...form, photo: file, photoPreview: URL.createObjectURL(file) });
  };

  const handleSave = async () => {
    setSaving(true); setError(null);

    let image_url = form.image_url;
    if (form.photo) {
      const compressed = await imageCompression(form.photo, { maxSizeMB: 0.8, maxWidthOrHeight: 1200, useWebWorker: true });
      const fileExt = form.photo.name.split('.').pop();
      const fileName = `inventory_${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage.from('plant-images').upload(fileName, compressed);
      if (!uploadError) {
        const { data: urlData } = supabase.storage.from('plant-images').getPublicUrl(fileName);
        image_url = urlData.publicUrl;
      }
    }

    const row = {
      seller_id: user.id,
      common_name: form.common_name,
      genus: form.genus || null,
      sale_type: form.sale_type || null,
      sale_size: form.sale_size || null,
      price: form.price || null,
      cost_price: form.cost_price || null,
      quantity: form.quantity ? parseInt(form.quantity) : null,
      notes: form.notes || null,
      status: form.status || 'available',
      image_url: image_url || null,
      variegation_level: isVariegated(form.common_name) ? (form.variegation_level || null) : null,
      variegation_pattern: isVariegated(form.common_name) ? (form.variegation_pattern || null) : null,
      is_public: form.is_public || false,
      image_url_2: form.image_url_2 || null,
      image_url_3: form.image_url_3 || null,
      shipping_fee: form.shipping_fee || null,
      estimated_shipping: form.estimated_shipping || null,
      returns_accepted: form.returns_accepted || false,
    };

    if (editingId) {
      const { error } = await supabase.from('inventory').update(row).eq('id', editingId);
      if (!error) { setInventory(inventory.map(i => i.id === editingId ? { ...i, ...row } : i)); setSuccess('Updated!'); }
      else { setError(error.message); }
    } else {
      const { data, error } = await supabase.from('inventory').insert(row).select();
      if (!error) { setInventory([data[0], ...inventory]); setSuccess('Plant added!'); }
      else { setError(error.message); }
    }

    setShowAddForm(false); setEditingId(null); setForm(emptyPlant()); setSaving(false);
    setTimeout(() => setSuccess(null), 3000);
  };

  const openSoldModal = (item) => {
    setSoldForm({ price: item.price ? item.price.replace(/[^0-9.]/g, '') : '', source: 'Palmstreet', date: new Date().toISOString().slice(0, 10) });
    setSoldModal({ item });
  };

  const confirmSold = async () => {
    const { item } = soldModal;
    if (!soldForm.price) { setError('Please enter a sale price.'); return; }
    if (!soldForm.date) { setError('Please enter a sale date.'); return; }
    const formattedPrice = `$${parseFloat(soldForm.price).toFixed(2)}`;
    const soldDateISO = new Date(soldForm.date + 'T12:00:00').toISOString();
    await supabase.from('inventory').update({ status: 'sold', date_sold: soldDateISO, price: formattedPrice }).eq('id', item.id);
    const plantRow = {
      common_name: item.common_name.trim(), sold_price: formattedPrice,
      date_sold: soldForm.date, source: soldForm.source, status: 'pending', country: 'USA',
    };
    if (item.genus) plantRow.genus = item.genus;
    if (item.sale_type) plantRow.sale_type = item.sale_type;
    if (item.sale_size) plantRow.sale_size = item.sale_size;
    await supabase.from('plants').insert(plantRow);
    setInventory(inventory.map(i => i.id === item.id ? { ...i, status: 'sold', price: formattedPrice, date_sold: soldDateISO } : i));
    setSuccess(`${item.common_name} marked as sold! Sale submitted for review.`);
    setSoldModal(null);
    setTimeout(() => setSuccess(null), 4000);
  };

  const handleStatusChange = async (item, newStatus) => {
    if (newStatus === 'sold') { openSoldModal(item); return; }
    await supabase.from('inventory').update({ status: newStatus }).eq('id', item.id);
    setInventory(inventory.map(i => i.id === item.id ? { ...i, status: newStatus } : i));
    setSuccess(`${item.common_name} marked as ${newStatus}.`);
    setTimeout(() => setSuccess(null), 4000);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this plant from inventory?')) return;
    await supabase.from('inventory').delete().eq('id', id);
    setInventory(inventory.filter(i => i.id !== id));
  };

  const toggleLive = async (item) => {
    const newVal = !item.is_public;
    if (newVal) {
      const rawPrice = String(item.price ?? '').replace(/[^0-9.]/g, '');
      const priceNum = rawPrice === '' ? 0 : parseFloat(rawPrice);
      if (isNaN(priceNum) || priceNum <= 0) {
        setError('A price greater than $0 is required before going live.');
        setTimeout(() => setError(null), 5000);
        return;
      }
    }
    await supabase.from('inventory').update({ is_public: newVal }).eq('id', item.id);
    setInventory(inventory.map(i => i.id === item.id ? { ...i, is_public: newVal } : i));
    setSuccess(newVal ? `${item.common_name} is now live on the For Sale page!` : `${item.common_name} removed from For Sale page.`);
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({ ...item, photo: null, photoPreview: item.image_url || null, quantity: item.quantity || '' });
    setShowAddForm(true);
    window.scrollTo(0, 0);
    fetchPlantPrices(item.common_name);
  };

  const handleSaveProfile = async () => {
    setSaving(true); setError(null);
    if (profileForm.business_name?.trim()) {
      const { data: existing } = await supabase.from('sellers').select('id').ilike('business_name', profileForm.business_name.trim()).neq('id', user.id);
      if (existing && existing.length > 0) {
        setError('That business name is already taken. Please choose a different name.');
        setSaving(false); return;
      }
    }
    const { error } = await supabase.from('sellers').update(profileForm).eq('id', user.id);
    if (!error) { setSeller({ ...seller, ...profileForm }); setSuccess('Profile saved!'); }
    else { setError(error.message); }
    setSaving(false);
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Delete your account? This removes all inventory data permanently.')) return;
    if (!window.confirm('Are you absolutely sure? This cannot be undone.')) return;
    await supabase.from('inventory').delete().eq('seller_id', user.id);
    await supabase.from('sellers').delete().eq('id', user.id);
    await supabase.auth.signOut();
    navigate('/');
  };

  const getFiltered = () => {
    let items = activeView === 'all' ? inventory : inventory.filter(i => i.status === activeView);
    if (filterSearch) items = items.filter(i => i.common_name?.toLowerCase().includes(filterSearch.toLowerCase()));
    if (filterType) items = items.filter(i => i.sale_type === filterType);
    if (filterSize) items = items.filter(i => i.sale_size === filterSize);
    return items;
  };

  const filtered = getFiltered();
  const totalCost = (items) => items.reduce((sum, i) => sum + parsePrice(i.cost_price) * (i.quantity || 1), 0);
  const totalResale = (items) => items.reduce((sum, i) => sum + parsePrice(i.price) * (i.quantity || 1), 0);
  const available = inventory.filter(i => i.status === 'available');
  const sold = inventory.filter(i => i.status === 'sold');
  const rehab = inventory.filter(i => i.status === 'rehab');
  const died = inventory.filter(i => i.status === 'died');

  const inp = {
    padding: mobile ? '10px 12px' : '8px 10px',
    border: '1.5px solid #c4a882', borderRadius: '8px',
    fontSize: mobile ? '16px' : '13px',
    background: '#ffffff', color: '#1e2d1f',
    fontFamily: 'Lato, sans-serif', boxSizing: 'border-box', width: '100%',
  };

  const tabStyle = (tab) => ({
    padding: mobile ? '10px 16px' : '8px 18px', borderRadius: '20px', cursor: 'pointer',
    fontSize: mobile ? '13px' : '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase',
    fontFamily: 'Lato, sans-serif', border: 'none',
    background: activeTab === tab ? 'linear-gradient(135deg, #2c4a2e, #4a7c59)' : 'rgba(44,74,46,0.08)',
    color: activeTab === tab ? '#f5f0e8' : '#4a7c59',
  });

  const stats = [
    { label: 'Available', count: available.length, view: 'available', color: '#4a7c59', items: available },
    { label: 'Sold', count: sold.length, view: 'sold', color: '#2c4a2e', items: sold },
    { label: 'Rehab', count: rehab.length, view: 'rehab', color: '#8b6914', items: rehab },
    { label: 'Died', count: died.length, view: 'died', color: '#c0392b', items: died },
    { label: 'All', count: inventory.length, view: 'all', color: '#4a5e4b', items: inventory },
  ];

  const labelStyle = { display: 'block', fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase', color: '#4a7c59', fontWeight: 700, marginBottom: '4px', fontFamily: 'Lato, sans-serif' };

  if (loading) return <p style={{ textAlign: 'center', padding: '80px', fontFamily: 'Lato, sans-serif', color: '#4a7c59' }}>Loading your dashboard...</p>;

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: mobile ? '16px 12px 80px' : '32px 16px 80px' }}>

      {/* SOLD MODAL */}
      {soldModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '20px', padding: '28px', maxWidth: '440px', width: '100%', boxShadow: '0 8px 40px rgba(0,0,0,0.3)' }}>
            <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '22px', color: '#1e3d20', marginBottom: '6px' }}>Mark as Sold </h3>
            <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '14px', color: '#4a5e4b', marginBottom: '20px' }}>{soldModal.item.common_name}</p>
            <div style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>Sale Price *</label>
              <input type="number" step="0.01" value={soldForm.price} onChange={(e) => setSoldForm({ ...soldForm, price: e.target.value })} placeholder="e.g. 45.00" style={inp} autoFocus />
            </div>
            <div style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>Date Sold *</label>
              <input type="date" value={soldForm.date} onChange={(e) => setSoldForm({ ...soldForm, date: e.target.value })} style={inp} />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Where Did You Sell It?</label>
              <select value={soldForm.source} onChange={(e) => setSoldForm({ ...soldForm, source: e.target.value })} style={inp}>
                {['Palmstreet', 'Facebook Marketplace', 'Etsy', 'eBay', 'Instagram', 'Local Sale', 'Other'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {error && <p style={{ color: '#c0392b', fontFamily: 'Lato, sans-serif', fontSize: '13px', marginBottom: '12px' }}>{error}</p>}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={confirmSold} style={{ flex: 1, background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)', color: '#f5f0e8', border: 'none', borderRadius: '12px', padding: '12px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}> Confirm Sale</button>
              <button onClick={() => { setSoldModal(null); setError(null); }} style={{ flex: 1, background: 'transparent', border: '2px solid #c4a882', color: '#4a5e4b', borderRadius: '12px', padding: '12px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Photo lightbox */}
      {selectedPhoto && (
        <div onClick={() => setSelectedPhoto(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.88)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: '16px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ position: 'relative', maxWidth: '90vw', maxHeight: '85vh' }}>
            <img src={selectedPhoto} alt="Plant" loading="lazy" style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: '16px', objectFit: 'contain', boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }} />
            <button onClick={() => setSelectedPhoto(null)} style={{ position: 'absolute', top: '10px', right: '10px', background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff', borderRadius: '50%', width: '36px', height: '36px', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}></button>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div style={{ fontSize: '11px', letterSpacing: '4px', color: '#4a7c59', textTransform: 'uppercase', marginBottom: '4px', fontFamily: 'Lato, sans-serif' }}> Member Dashboard</div>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: mobile ? '22px' : '26px', color: '#1e3d20', margin: 0 }}>{seller?.business_name || 'My Dashboard'}</h1>
        </div>
        <button onClick={handleLogout} style={{ background: 'transparent', border: '2px solid #c0392b', color: '#c0392b', borderRadius: '20px', padding: '8px 16px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>Log Out</button>
      </div>

      {/* Stat boxes */}
      <div style={{ display: 'grid', gridTemplateColumns: mobile ? 'repeat(3, 1fr)' : 'repeat(5, 1fr)', gap: '8px', marginBottom: '20px' }}>
        {stats.map(({ label, count, view, color, items }) => (
          <div key={view} onClick={() => { setActiveView(view); setActiveTab('inventory'); }}
            style={{ background: activeView === view ? color : '#fff', borderRadius: '14px', padding: mobile ? '12px 8px' : '16px', textAlign: 'center', boxShadow: '0 2px 12px rgba(44,74,46,0.08)', border: `2px solid ${activeView === view ? color : 'rgba(143,185,154,0.3)'}`, cursor: 'pointer', transition: 'all 0.2s' }}>
            <div style={{ fontFamily: 'Playfair Display, serif', fontSize: mobile ? '22px' : '28px', color: activeView === view ? '#fff' : color, fontWeight: 700 }}>{count}</div>
            <div style={{ fontFamily: 'Lato, sans-serif', fontSize: mobile ? '9px' : '11px', color: activeView === view ? 'rgba(255,255,255,0.8)' : '#8b9e8c', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '4px' }}>{label}</div>
            {!mobile && totalCost(items) > 0 && <div style={{ fontSize: '11px', color: activeView === view ? 'rgba(255,255,255,0.9)' : '#4a5e4b', fontFamily: 'Lato, sans-serif', marginTop: '4px' }}>Cost: ${totalCost(items).toFixed(2)}</div>}
            {!mobile && totalResale(items) > 0 && <div style={{ fontSize: '11px', color: activeView === view ? 'rgba(255,255,255,0.9)' : '#4a7c59', fontFamily: 'Lato, sans-serif', fontWeight: 700 }}>Value: ${totalResale(items).toFixed(2)}</div>}
          </div>
        ))}
      </div>

      {success && <div style={{ background: '#f0f7f1', border: '1px solid #4a7c59', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px' }}><p style={{ color: '#2c4a2e', fontFamily: 'Lato, sans-serif', fontSize: '14px', margin: 0 }}> {success}</p></div>}
      {error && !soldModal && <div style={{ background: '#fff5f5', border: '1px solid #c0392b', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px' }}><p style={{ color: '#c0392b', fontFamily: 'Lato, sans-serif', fontSize: '14px', margin: 0 }}>{error}</p></div>}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <button style={tabStyle('inventory')} onClick={() => setActiveTab('inventory')}>Inventory</button>
        <button style={tabStyle('profile')} onClick={() => setActiveTab('profile')}>Profile</button>
        <button style={{ ...tabStyle('messages'), position: 'relative' }} onClick={async () => {
          setActiveTab('messages');
          if (messages.length === 0) {
            setMessagesLoading(true);
            const { data } = await supabase.from('messages').select('*').eq('seller_id', user.id).order('created_at', { ascending: false });
            setMessages(data || []);
            setMessagesLoading(false);
          }
        }}>
          Messages {messages.filter(m => !m.is_read).length > 0 && <span style={{ background: '#c0392b', color: '#fff', borderRadius: '50%', width: '16px', height: '16px', fontSize: '10px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginLeft: '4px' }}>{messages.filter(m => !m.is_read).length}</span>}
        </button>
        <button style={tabStyle('storefront')} onClick={() => window.open(`/shop/${slugify(seller?.business_name || '')}`, '_blank')}>View My Shop ↗</button>
      </div>

      {activeTab === 'inventory' && (
        <div>
          {/* Add / Edit form */}
          {showAddForm && (
            <div style={{ background: '#fff', borderRadius: '16px', padding: mobile ? '16px' : '20px', boxShadow: '0 2px 16px rgba(44,74,46,0.1)', marginBottom: '20px', border: '2px solid #4a7c59' }}>
              <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#1e3d20', marginBottom: '16px' }}>{editingId ? 'Edit Edit Plant' : '+ Add Plant'}</h3>

              <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(2, 1fr)', gap: '12px', marginBottom: '12px' }}>

                {/* Plant name with autocomplete — full width */}
                <div style={{ position: 'relative', gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Plant Name *</label>
                  <input name="common_name" value={form.common_name} onChange={(e) => { handleFormChange(e); setSuggestionIndex(-1); }}
                    onBlur={() => setTimeout(() => { setSuggestions([]); setSuggestionIndex(-1); }, 200)}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown') { e.preventDefault(); setSuggestionIndex(i => Math.min(i + 1, suggestions.length - 1)); }
                      else if (e.key === 'ArrowUp') { e.preventDefault(); setSuggestionIndex(i => Math.max(i - 1, -1)); }
                      else if (e.key === 'Enter' && suggestionIndex >= 0 && suggestions[suggestionIndex]) {
                        e.preventDefault();
                        const selected = suggestions[suggestionIndex];
                        const match = plantRecords.find(p => p.common_name === selected);
                        setForm(prev => ({
                          ...prev,
                          common_name: selected,
                          genus: match?.genus || prev.genus,
                          scientific_name: match?.scientific_name || prev.scientific_name,
                        }));
                        setSuggestions([]); setSuggestionIndex(-1);
                        fetchPlantPrices(selected);
                      }
                      else if (e.key === 'Escape') { setSuggestions([]); setSuggestionIndex(-1); }
                    }}
                    placeholder="e.g. Monstera Albo" style={inp} autoComplete="off" />
                  {suggestions.length > 0 && (
                    <ul style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '2px solid #4a7c59', borderRadius: '8px', listStyle: 'none', margin: '2px 0 0', padding: '4px', zIndex: 1000, maxHeight: '200px', overflowY: 'auto', boxShadow: '0 4px 16px rgba(44,74,46,0.15)' }}>
                      {suggestions.map((name, idx) => (
                        <li key={name} onMouseDown={() => {
                            const match = plantRecords.find(p => p.common_name === name);
                            setForm(prev => ({
                              ...prev,
                              common_name: name,
                              genus: match?.genus || prev.genus,
                              scientific_name: match?.scientific_name || prev.scientific_name,
                            }));
                            setSuggestions([]); setSuggestionIndex(-1); fetchPlantPrices(name);
                          }}
                          onMouseEnter={() => setSuggestionIndex(idx)}
                          style={{ padding: mobile ? '12px 10px' : '8px 10px', cursor: 'pointer', borderRadius: '6px', fontSize: mobile ? '15px' : '13px', fontFamily: 'Lato, sans-serif', background: suggestionIndex === idx ? '#f0f7f1' : 'transparent', fontWeight: suggestionIndex === idx ? 700 : 400 }}>{name}</li>
                      ))}
                    </ul>
                  )}
                </div>

                <div>
                  <label style={labelStyle}>Genus</label>
                  <input name="genus" value={form.genus} onChange={handleFormChange} placeholder="e.g. Monstera" style={inp} />
                </div>

                <div>
                  <label style={labelStyle}>Scientific Name</label>
                  <input name="scientific_name" value={form.scientific_name || ''} onChange={handleFormChange} placeholder="e.g. Monstera deliciosa" style={inp} />
                </div>

                <div>
                  <label style={labelStyle}>Type</label>
                  <select name="sale_type" value={form.sale_type} onChange={(e) => {
                    const newType = e.target.value;
                    setForm(f => ({
                      ...f,
                      sale_type: newType,
                      sale_size: newType !== 'Potted' ? '' : f.sale_size,
                      quantity: f.quantity || '1',
                    }));
                  }} style={inp}>
                    <option value="">Select type...</option>
                    {SALE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>

                {/* Pot Size — only when Potted */}
                {form.sale_type === 'Potted' && (
                  <div>
                    <label style={labelStyle}>Pot Size</label>
                    <select name="sale_size" value={form.sale_size} onChange={handleFormChange} style={inp}>
                      <option value="">Select size...</option>
                      {POT_SIZES.map(s => <option key={s} value={s}>{s}"</option>)}
                    </select>
                  </div>
                )}

                {/* TC Pack Size — only when Tissue Culture */}
                {form.sale_type === 'Tissue Culture (TC)' && (
                  <div>
                    <label style={labelStyle}>Pack Size</label>
                    <select name="quantity" value={form.quantity || '1'} onChange={handleFormChange} style={inp}>
                      {Array.from({ length: 20 }, (_, i) => i + 1).map(n => (
                        <option key={n} value={String(n)}>{n} {n === 1 ? 'plant' : 'plants'}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label style={labelStyle}>Cost Price</label>
                  <input name="cost_price" value={form.cost_price} onChange={handleFormChange} placeholder="$0.00" style={inp} />
                </div>

                <div>
                  <label style={{ ...labelStyle, color: form.is_public ? '#8b6914' : undefined }}>
                    Resale Price * {form.is_public && <span style={{ fontSize: '10px', background: '#8b6914', color: '#fff', padding: '2px 6px', borderRadius: '8px', marginLeft: '4px', fontWeight: 700 }}>Shown publicly</span>}
                  </label>
                  <input name="price" value={form.price} onChange={handleFormChange} placeholder="$0.00"
                    style={{ ...inp, border: form.is_public ? '2px solid #8b6914' : inp.border, fontWeight: form.is_public ? 700 : 400 }} />
                </div>

                {/* Quantity — hidden for TC since Pack Size handles it, auto defaults to 1 */}
                {form.sale_type !== 'Tissue Culture (TC)' && (
                  <div>
                    <label style={labelStyle}>Quantity</label>
                    <input name="quantity" type="number" min="1" value={form.quantity || '1'} onChange={handleFormChange} style={inp} />
                  </div>
                )}

                <div>
                  <label style={labelStyle}>Status</label>
                  <select name="status" value={form.status} onChange={handleFormChange} style={inp}>
                    {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>

                {/* Variegation — only shown if plant is marked variegated in admin */}
                {isVariegated(form.common_name) && (
                  <>
                    <div>
                      <label style={labelStyle}>Variegation Level</label>
                      <select name="variegation_level" value={form.variegation_level} onChange={handleFormChange} style={inp}>
                        <option value="">Select level...</option>
                        <option value="Low"> Low — mostly green</option>
                        <option value="Mid"> Mid — balanced mix</option>
                        <option value="High"> High — heavily variegated</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Variegation Pattern</label>
                      <select name="variegation_pattern" value={form.variegation_pattern} onChange={handleFormChange} style={inp}>
                        <option value="">Select pattern...</option>
                        <option value="Half Moon">Half Moon</option>
                        <option value="Marble">Marble</option>
                        <option value="Speckled/Splash">Speckled/Splash</option>
                        <option value="Sectoral">Sectoral</option>
                        <option value="Tricolor">Tricolor</option>
                        <option value="Ghost">Ghost</option>
                        <option value="Full">Full</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </>
                )}

                {/* Market price reference */}
                {((form.common_name && form.sale_type && form.sale_type !== 'Potted') || (form.common_name && form.sale_type === 'Potted' && form.sale_size)) ? (
                  <div style={{ gridColumn: '1 / -1' }}>
                    {(() => {
                      const range = getPriceRange(form.sale_type, form.sale_size);
                      if (priceLoading) return <div style={{ padding: '10px 14px', background: '#f7f3ec', borderRadius: '10px', fontFamily: 'Lato, sans-serif', fontSize: '12px', color: '#8b9e8c' }}>Loading market prices...</div>;
                      if (!range) return <div style={{ padding: '10px 14px', background: '#f7f3ec', borderRadius: '10px', fontFamily: 'Lato, sans-serif', fontSize: '12px', color: '#8b9e8c' }}>No sales data found for this type/size yet.</div>;
                      return (
                        <div style={{ padding: '10px 14px', background: 'linear-gradient(135deg, #f0f7f1, #e8f4eb)', border: '1px solid rgba(74,124,89,0.3)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                          <div>
                            <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase', color: '#4a7c59', fontWeight: 700 }}> Market Price ({range.count} sales)</span>
                            <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '18px', color: '#1e3d20', fontWeight: 700, marginTop: '2px' }}>
                              ${range.min.toFixed(0)} – ${range.max.toFixed(0)}
                              <span style={{ fontSize: '12px', color: '#4a7c59', fontFamily: 'Lato, sans-serif', fontWeight: 400, marginLeft: '8px' }}>avg ${range.avg}</span>
                            </div>
                          </div>
                          <a href={`/plant/${slugify(form.common_name)}`} target="_blank" rel="noreferrer"
                            style={{ fontFamily: 'Lato, sans-serif', fontSize: '12px', color: '#4a7c59', fontWeight: 700, textDecoration: 'none', background: '#fff', border: '1px solid #4a7c59', borderRadius: '8px', padding: '6px 12px', whiteSpace: 'nowrap' }}>
                            View Details →
                          </a>
                        </div>
                      );
                    })()}
                  </div>
                ) : null}

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Notes</label>
                  <input name="notes" value={form.notes} onChange={handleFormChange} placeholder="e.g. High variegation, ships in 3 days..." style={inp} />
                </div>




              {/* PUBLIC LISTING SETTINGS */}
                <div style={{ gridColumn: '1 / -1', background: '#f0f7f1', borderRadius: '12px', padding: '14px', border: '1px solid rgba(74,124,89,0.3)' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: form.is_public ? '12px' : '0' }}>
                    <div onClick={() => {
                        const rawPrice = String(form.price ?? '').replace(/[^0-9.]/g, '');
                        const priceNum = rawPrice === '' ? 0 : parseFloat(rawPrice);
                        if (!form.is_public && (isNaN(priceNum) || priceNum <= 0)) {
                          setError('A price greater than $0 is required before going live.');
                          setTimeout(() => setError(null), 5000);
                          return;
                        }
                        setForm(f => ({ ...f, is_public: !f.is_public }));
                      }}
                      style={{ width: '44px', height: '24px', borderRadius: '12px', background: form.is_public ? '#4a7c59' : '#c4a882', position: 'relative', transition: 'background 0.2s', flexShrink: 0, cursor: 'pointer' }}>
                      <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '2px', left: form.is_public ? '22px' : '2px', transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }} />
                    </div>
                    <div>
                      <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', fontWeight: 700, color: '#1e3d20' }}>{form.is_public ? 'Listed on For Sale page' : 'Not listed publicly'}</div>
                      <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '11px', color: '#4a5e4b' }}>Toggle on to show this plant on the public For Sale page</div>
                    </div>
                  </label>
                  {form.is_public && (
                    <div style={{ marginTop: '12px' }}>
                      <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '12px', color: '#4a5e4b', marginBottom: '10px' }}>Contact details are pulled from your Profile tab.</p>
                      <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap: '10px' }}>
                        <div>
                          <label style={labelStyle}>Shipping Fee</label>
                          <input name="shipping_fee" value={form.shipping_fee || ''} onChange={handleFormChange} placeholder="e.g. $15 or Free" style={inp} />
                        </div>
                        <div>
                          <label style={labelStyle}>Estimated Ship Date</label>
                          <input name="estimated_shipping" value={form.estimated_shipping || ''} onChange={handleFormChange} placeholder="e.g. Mon/Tue/Wed" style={inp} />
                        </div>
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', cursor: 'pointer' }}>
                        <input type="checkbox" checked={form.returns_accepted || false} onChange={(e) => setForm(f => ({ ...f, returns_accepted: e.target.checked }))} style={{ width: '16px', height: '16px' }} />
                        <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '12px', color: '#1e3d20', fontWeight: 700 }}>Returns & exchanges accepted</span>
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* PHOTOS — 3 slots side by side */}
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase', color: '#4a7c59', fontWeight: 700, marginBottom: '8px', fontFamily: 'Lato, sans-serif' }}>Photos</label>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {/* Photo 1 — required */}
                  <div>
                    <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: `2px dashed ${form.photoPreview ? '#4a7c59' : '#c0392b'}`, borderRadius: '10px', width: '90px', height: '90px', cursor: 'pointer', background: '#f7f3ec', boxSizing: 'border-box', position: 'relative' }}>
                      <input type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: 'none' }} />
                      {form.photoPreview ? (
                        <img src={form.photoPreview} alt="Main" style={{ width: '90px', height: '90px', objectFit: 'cover', borderRadius: '8px', display: 'block' }} />
                      ) : (
                        <>
                          <span style={{ fontSize: '20px', marginBottom: '2px' }}>+</span>
                          <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '9px', color: '#c0392b', fontWeight: 700, textAlign: 'center' }}>Photo (required)</span>
                        </>
                      )}
                    </label>
                  </div>
                  {/* Photos 2 & 3 — optional, always shown */}
                  {[2, 3].map((num) => {
                    const key = `image_url_${num}`;
                    return (
                      <div key={num} style={{ position: 'relative' }}>
                        {form[key] ? (
                          <>
                            <img src={form[key]} alt={`View ${num}`} style={{ width: '90px', height: '90px', objectFit: 'cover', borderRadius: '10px', display: 'block', border: '2px solid #4a7c59' }} />
                            <button type="button" onClick={() => setForm(f => ({ ...f, [key]: '' }))}
                              style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(192,57,43,0.85)', border: 'none', color: '#fff', borderRadius: '50%', width: '22px', height: '22px', fontSize: '11px', cursor: 'pointer', lineHeight: 1, fontWeight: 700 }}>X</button>
                          </>
                        ) : (
                          <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '2px dashed #c4a882', borderRadius: '10px', width: '90px', height: '90px', cursor: 'pointer', background: '#f7f3ec', boxSizing: 'border-box' }}>
                            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => {
                              const file = e.target.files[0];
                              if (!file) return;
                              const fileExt = file.name.split('.').pop().toLowerCase();
                              const safeExt = ['jpg','jpeg','png','webp'].includes(fileExt) ? fileExt : 'jpg';
                              const rand = Math.random().toString(36).slice(2,7);
                              const fileName = `${Date.now()}_${rand}_extra${num}.${safeExt}`;
                              const { error: upErr } = await supabase.storage.from('plant-images').upload(fileName, file, { contentType: file.type || 'image/jpeg', upsert: false });
                              if (!upErr) {
                                const { data: urlData } = supabase.storage.from('plant-images').getPublicUrl(fileName);
                                setForm(f => ({ ...f, [key]: urlData.publicUrl }));
                              } else {
                                alert('Photo upload failed: ' + upErr.message);
                              }
                              e.target.value = '';
                            }} />
                            <span style={{ fontSize: '18px', marginBottom: '2px' }}>+</span>
                            <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '9px', color: '#8b9e8c', fontWeight: 700, textAlign: 'center' }}>Photo {num}{'\n'}(optional)</span>
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button onClick={handleSave} disabled={saving} style={{ flex: 1, background: saving ? '#8fb99a' : 'linear-gradient(135deg, #2c4a2e, #4a7c59)', color: '#f5f0e8', border: 'none', borderRadius: '10px', padding: mobile ? '14px' : '10px 20px', fontSize: '14px', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'Lato, sans-serif' }}>
                  {saving ? 'Saving...' : editingId ? ' Update' : '+ Add Plant'}
                </button>
                <button onClick={() => { setShowAddForm(false); setEditingId(null); setForm(emptyPlant()); setPlantPriceData([]); }} style={{ flex: 1, background: 'transparent', border: '2px solid #c4a882', color: '#4a5e4b', borderRadius: '10px', padding: mobile ? '14px' : '10px 20px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {!showAddForm && (
            <button onClick={() => setShowAddForm(true)} style={{ marginBottom: '16px', background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)', color: '#f5f0e8', border: 'none', borderRadius: '12px', padding: mobile ? '14px 24px' : '10px 24px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif', letterSpacing: '1px', width: mobile ? '100%' : 'auto' }}>
              + Add Plant
            </button>
          )}

          {/* Filters */}
          {activeView === 'all' && (
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px', alignItems: 'center' }}>
              <input value={filterSearch} onChange={(e) => setFilterSearch(e.target.value)} placeholder="Search plant name..."
                style={{ ...inp, flex: 1, minWidth: '140px' }} />
              <select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={{ ...inp, width: 'auto' }}>
                <option value="">All Types</option>
                <option value="Potted">Potted</option>
                <option value="Cutting">Cutting</option>
                <option value="Tissue Culture (TC)">Tissue Culture (TC)</option>
              </select>
              {(filterSearch || filterType || filterSize) && (
                <button onClick={() => { setFilterSearch(''); setFilterType(''); setFilterSize(''); }}
                  style={{ padding: '8px 12px', background: 'transparent', border: '1px solid #c0392b', color: '#c0392b', borderRadius: '8px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif', whiteSpace: 'nowrap' }}>
                  Clear
                </button>
              )}
            </div>
          )}

          {/* View label + totals */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
            <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: mobile ? '18px' : '20px', color: '#1e3d20', margin: 0 }}>
              {activeView === 'all' ? 'All Inventory' : activeView.charAt(0).toUpperCase() + activeView.slice(1)} ({filtered.length})
            </h3>
            {!mobile && filtered.length > 0 && (
              <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#4a5e4b', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                {totalCost(filtered) > 0 && <span>Cost: <strong style={{ color: '#c0392b' }}>${totalCost(filtered).toFixed(2)}</strong></span>}
                {totalResale(filtered) > 0 && <span>Value: <strong style={{ color: '#4a7c59' }}>${totalResale(filtered).toFixed(2)}</strong></span>}
                {totalCost(filtered) > 0 && totalResale(filtered) > 0 && <span>Profit: <strong style={{ color: totalResale(filtered) - totalCost(filtered) >= 0 ? '#2c4a2e' : '#c0392b' }}>${(totalResale(filtered) - totalCost(filtered)).toFixed(2)}</strong></span>}
              </div>
            )}
          </div>

          {/* Inventory — mobile cards or desktop table */}
          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px', background: '#fff', borderRadius: '16px', boxShadow: '0 2px 12px rgba(44,74,46,0.06)' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}></div>
              <p style={{ color: '#4a5e4b', fontFamily: 'Lato, sans-serif' }}>No plants in this category yet.</p>
            </div>
          ) : mobile ? (
            // MOBILE: card layout
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {filtered.map((item) => (
                <div key={item.id} style={{ background: '#fff', borderRadius: '14px', padding: '14px', boxShadow: '0 2px 10px rgba(44,74,46,0.08)', border: '1px solid rgba(143,185,154,0.3)' }}>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    {item.image_url
                      ? <img src={item.image_url} alt={item.common_name} onClick={() => setSelectedPhoto(item.image_url)} style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '10px', flexShrink: 0, cursor: 'pointer' }} />
                      : <div style={{ width: '60px', height: '60px', background: '#f0f7f1', borderRadius: '10px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px' }}></div>
                    }
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div onClick={() => handleEdit(item)} style={{ fontFamily: 'Playfair Display, serif', fontSize: '16px', color: '#1e3d20', fontWeight: 700, marginBottom: '2px', cursor: 'pointer', textDecoration: 'underline dotted' }}>{item.common_name}</div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                        {item.sale_type && <span style={{ fontSize: '11px', color: '#4a7c59', fontFamily: 'Lato, sans-serif' }}>{item.sale_type}</span>}
                        {item.sale_size && <span style={{ fontSize: '11px', color: '#4a7c59', fontFamily: 'Lato, sans-serif' }}>{item.sale_size}"</span>}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                        {item.cost_price && <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#c0392b' }}>Cost: {formatPrice(item.cost_price)}</span>}
                        {item.price && <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '15px', color: '#4a7c59', fontWeight: 700 }}>{formatPrice(item.price)}</span>}
                      </div>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                        {item.status === 'available' && <button onClick={() => toggleLive(item)} style={{ padding: '7px 12px', background: item.is_public ? 'linear-gradient(135deg, #4a7c59, #2c4a2e)' : 'transparent', border: item.is_public ? 'none' : '1px solid #4a7c59', color: item.is_public ? '#f5f0e8' : '#4a7c59', borderRadius: '8px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>{item.is_public ? 'Live' : 'Go Live'}</button>}
                        <select value={item.status} onChange={(e) => handleStatusChange(item, e.target.value)}
                          style={{ padding: '7px 10px', border: '1px solid #c4a882', borderRadius: '8px', fontSize: '11px', fontFamily: 'Lato, sans-serif', background: '#fff', cursor: 'pointer' }}>
                          <option value="available">Available</option>
                          <option value="sold">Sold</option>
                          <option value="rehab">Rehab</option>
                          <option value="died">Died</option>
                        </select>
                        <button onClick={() => window.open(`/listing/${item.id}/${item.sale_type ? item.sale_type.toLowerCase().replace(/[^a-z0-9]+/g,'-') : ''}${item.sale_size ? '-'+item.sale_size+'in' : ''}-${(item.common_name||'').toLowerCase().replace(/[^a-z0-9]+/g,'-')}`, '_blank')} style={{ padding: '7px 12px', background: 'transparent', border: '1px solid #4a7c59', color: '#4a7c59', borderRadius: '8px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>View ↗</button>
                        <button onClick={(e) => {
                          const price = item.price ? '$' + parseFloat(String(item.price).replace(/[^0-9.]/g,'')).toFixed(2) : '';
                          const url = `https://trueplantvalue.com/listing/${item.id}/${item.sale_type ? item.sale_type.toLowerCase().replace(/[^a-z0-9]+/g,'-') : ''}${item.sale_size ? '-'+item.sale_size+'in' : ''}-${(item.common_name||'').toLowerCase().replace(/[^a-z0-9]+/g,'-')}`;
                          const text = [`${item.common_name} — ${price}`, [item.sale_type, item.sale_size ? `${item.sale_size}"` : null].filter(Boolean).join(' '), '', item.notes || '', '', 'View full listing:', url].join('\n').replace(/\n{3,}/g,'\n\n').trim();
                          navigator.clipboard.writeText(text).then(() => { const btn = e.currentTarget; btn.textContent = 'Copied!'; setTimeout(() => { btn.textContent = 'Copy'; }, 2000); });
                        }} style={{ padding: '7px 10px', background: 'transparent', border: '1px solid #8b6914', color: '#8b6914', borderRadius: '8px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>Copy</button>
                        <button onClick={() => handleDelete(item.id)} title="Delete" style={{ padding: '7px 10px', background: 'transparent', border: '1px solid #c0392b', color: '#c0392b', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>🗑</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // DESKTOP: spreadsheet table
            <div style={{ overflowX: 'auto', borderRadius: '16px', boxShadow: '0 2px 16px rgba(44,74,46,0.08)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', fontSize: '13px', fontFamily: 'Lato, sans-serif' }}>
                <thead>
                  <tr style={{ background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)' }}>
                    {['Photo', 'Plant', 'Type', 'Size', 'Cost', 'Price', 'Qty', 'Status', 'Live', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '10px 12px', color: '#f5f0e8', textAlign: 'left', fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item, index) => (
                    <tr key={item.id} style={{ borderBottom: '1px solid rgba(143,185,154,0.2)', background: index % 2 === 0 ? '#ffffff' : '#f9fcf9' }}>
                      <td style={{ padding: '10px 12px' }}>
                        {item.image_url
                          ? <img src={item.image_url} alt={item.common_name} onClick={() => setSelectedPhoto(item.image_url)} style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '6px', cursor: 'pointer' }} />
                          : <div style={{ width: '40px', height: '40px', background: '#f0f7f1', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}></div>
                        }
                      </td>
                      <td onClick={() => handleEdit(item)} style={{ padding: '8px 8px', fontWeight: 700, color: '#1e3d20', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', textDecoration: 'underline dotted' }}>{item.common_name}</td>
                      <td style={{ padding: '8px 8px', color: '#4a5e4b' }}>{item.sale_type || '—'}</td>
                      <td style={{ padding: '8px 8px', color: '#4a5e4b' }}>{item.sale_size ? `${item.sale_size}"` : '—'}</td>
                      <td style={{ padding: '8px 6px', color: '#c0392b', fontWeight: 700 }}>{item.cost_price ? formatPrice(item.cost_price) : '—'}</td>
                      <td style={{ padding: '8px 6px', color: '#4a7c59', fontWeight: 700 }}>{item.price ? formatPrice(item.price) : '—'}</td>
                      <td style={{ padding: '8px 8px', color: '#4a5e4b', textAlign: 'center' }}>{item.quantity || '—'}</td>

                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ padding: '3px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 700, background: item.status === 'available' ? '#f0f7f1' : item.status === 'sold' ? '#f0f0f0' : item.status === 'died' ? '#fff0f0' : '#fdf8f0', color: item.status === 'available' ? '#4a7c59' : item.status === 'sold' ? '#2c4a2e' : item.status === 'died' ? '#c0392b' : '#8b6914' }}>
                          {item.status}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        {item.status === 'available' && (
                          <button onClick={() => toggleLive(item)} style={{ padding: '4px 10px', background: item.is_public ? 'linear-gradient(135deg, #4a7c59, #2c4a2e)' : 'transparent', border: item.is_public ? 'none' : '1px solid #4a7c59', color: item.is_public ? '#f5f0e8' : '#4a7c59', borderRadius: '12px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif', whiteSpace: 'nowrap' }}>
                            {item.is_public ? 'Live' : 'Go Live'}
                          </button>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: '3px' }}>
                          <button onClick={() => window.open(`/listing/${item.id}/${item.sale_type ? item.sale_type.toLowerCase().replace(/[^a-z0-9]+/g,'-') : ''}${item.sale_size ? '-'+item.sale_size+'in' : ''}-${(item.common_name||'').toLowerCase().replace(/[^a-z0-9]+/g,'-')}`, '_blank')} title="View listing" style={{ padding: '4px 8px', background: 'transparent', border: '1px solid #4a7c59', color: '#4a7c59', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}>View ↗</button>
                          <select value={item.status} onChange={(e) => handleStatusChange(item, e.target.value)}
                            style={{ padding: '4px 6px', border: '1px solid #c4a882', borderRadius: '6px', fontSize: '11px', fontFamily: 'Lato, sans-serif', background: '#fff', cursor: 'pointer' }}>
                            <option value="available">Available</option>
                            <option value="sold">Sold</option>
                            <option value="rehab">Rehab</option>
                            <option value="died">Died</option>
                          </select>
                          <button title="Copy for Marketplace" onClick={(e) => {
                            const price = item.price ? '$' + parseFloat(String(item.price).replace(/[^0-9.]/g,'')).toFixed(2) : '';
                            const url = `https://trueplantvalue.com/listing/${item.id}/${item.sale_type ? item.sale_type.toLowerCase().replace(/[^a-z0-9]+/g,'-') : ''}${item.sale_size ? '-'+item.sale_size+'in' : ''}-${(item.common_name||'').toLowerCase().replace(/[^a-z0-9]+/g,'-')}`;
                            const text = [`${item.common_name} — ${price}`, [item.sale_type, item.sale_size ? `${item.sale_size}"` : null].filter(Boolean).join(' '), '', 'View full listing:', url].join('\n').trim();
                            navigator.clipboard.writeText(text).then(() => { const btn = e.currentTarget; btn.textContent = 'Copied!'; setTimeout(() => { btn.textContent = 'Copy'; }, 2000); });
                          }} style={{ padding: '4px 6px', background: 'transparent', border: '1px solid #8b6914', color: '#8b6914', borderRadius: '6px', fontSize: '10px', cursor: 'pointer' }}>Copy</button>
                          <button onClick={() => handleDelete(item.id)} title="Delete" style={{ padding: '4px 6px', background: 'transparent', border: '1px solid #c0392b', color: '#c0392b', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}>🗑</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Messages Tab */}
      {activeTab === 'messages' && (
        <div>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '24px', color: '#1e3d20', marginBottom: '8px' }}>Messages</h2>
          <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#4a5e4b', marginBottom: '24px' }}>Messages from buyers interested in your listings.</p>
          {messagesLoading ? <p style={{ color: '#4a7c59', fontStyle: 'italic', fontFamily: 'Lato, sans-serif' }}>Loading messages...</p>
          : messages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px', background: '#fff', borderRadius: '20px', border: '1px solid rgba(143,185,154,0.3)' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}></div>
              <p style={{ fontFamily: 'Lato, sans-serif', color: '#4a5e4b' }}>No messages yet. Once buyers message you, they'll appear here.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {messages.map((msg) => (
                <div key={msg.id} style={{ background: '#fff', borderRadius: '16px', padding: '18px 20px', boxShadow: '0 2px 10px rgba(44,74,46,0.08)', border: `1px solid ${msg.is_read ? 'rgba(143,185,154,0.3)' : '#4a7c59'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '14px', fontWeight: 700, color: '#1e3d20' }}>{msg.buyer_name}</div>
                      <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '12px', color: '#8b9e8c' }}>{msg.buyer_email} · {new Date(msg.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                    </div>
                    {!msg.is_read && <span style={{ background: '#4a7c59', color: '#fff', borderRadius: '12px', padding: '2px 10px', fontSize: '10px', fontWeight: 700, fontFamily: 'Lato, sans-serif' }}>NEW</span>}
                  </div>
                  <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '14px', color: '#1e3d20', lineHeight: 1.6, marginBottom: '12px', background: '#f7f3ec', padding: '12px', borderRadius: '10px' }}>{msg.message}</p>
                  {msg.reply && (
                    <div style={{ background: '#f0f7f1', borderRadius: '10px', padding: '10px 14px', marginBottom: '12px', borderLeft: '3px solid #4a7c59' }}>
                      <div style={{ fontSize: '10px', color: '#4a7c59', fontWeight: 700, fontFamily: 'Lato, sans-serif', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '1px' }}>Your Reply</div>
                      <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#1e3d20', lineHeight: 1.5, margin: 0 }}>{msg.reply}</p>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button onClick={async () => {
                      await supabase.from('messages').update({ is_read: true }).eq('id', msg.id);
                      setMessages(messages.map(m => m.id === msg.id ? { ...m, is_read: true } : m));
                    }} style={{ padding: '6px 14px', background: 'transparent', border: '1px solid #c4a882', color: '#8b9e8c', borderRadius: '8px', fontSize: '12px', cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>Mark Read</button>
                    <button onClick={() => { setReplyingTo(msg.id); setReplyText(msg.reply || ''); }}
                      style={{ padding: '6px 14px', background: '#f0f7f1', border: '1px solid #4a7c59', color: '#4a7c59', borderRadius: '8px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>
                      {msg.reply ? 'Edit Reply' : 'Reply'}
                    </button>
                    <a href={`mailto:${msg.buyer_email}?subject=Re: ${msg.listing_id}&body=${encodeURIComponent(msg.message)}`}
                      style={{ padding: '6px 14px', background: 'transparent', border: '1px solid #8b6914', color: '#8b6914', borderRadius: '8px', fontSize: '12px', fontWeight: 700, fontFamily: 'Lato, sans-serif', textDecoration: 'none' }}>
                      Email Directly →
                    </a>
                  </div>
                  {replyingTo === msg.id && (
                    <div style={{ marginTop: '12px' }}>
                      <textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Type your reply..." rows={3}
                        style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #4a7c59', borderRadius: '10px', fontSize: '13px', fontFamily: 'Lato, sans-serif', boxSizing: 'border-box', resize: 'vertical', outline: 'none', marginBottom: '8px' }} />
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button disabled={replySaving} onClick={async () => {
                          setReplySaving(true);
                          await supabase.from('messages').update({ reply: replyText, replied_at: new Date().toISOString(), is_read: true }).eq('id', msg.id);
                          setMessages(messages.map(m => m.id === msg.id ? { ...m, reply: replyText, is_read: true } : m));
                          setReplyingTo(null); setReplyText('');
                          setReplySaving(false);
                        }} style={{ background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)', color: '#f5f0e8', border: 'none', borderRadius: '8px', padding: '8px 18px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>
                          {replySaving ? 'Saving...' : 'Save Reply'}
                        </button>
                        <button onClick={() => setReplyingTo(null)} style={{ background: 'transparent', border: '1px solid #c4a882', color: '#8b9e8c', borderRadius: '8px', padding: '8px 14px', fontSize: '12px', cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div style={{ background: '#fff', borderRadius: '20px', padding: mobile ? '20px' : '28px', boxShadow: '0 2px 16px rgba(44,74,46,0.08)', border: '1px solid rgba(143,185,154,0.3)' }}>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '24px', color: '#1e3d20', marginBottom: '24px' }}>Your Seller Profile</h2>

          {/* TOP ROW: Photo + Name/Tagline/Bio */}
          <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', alignItems: 'flex-start', flexWrap: mobile ? 'wrap' : 'nowrap' }}>
            {/* Profile Photo Upload */}
            <div style={{ flexShrink: 0 }}>
              <label style={labelStyle}>Photo</label>
              {profileForm.profile_photo_url ? (
                <div style={{ position: 'relative' }}>
                  <img src={profileForm.profile_photo_url} alt="Profile" style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', display: 'block', border: '3px solid rgba(143,185,154,0.4)' }} />
                  <label style={{ position: 'absolute', bottom: 0, right: 0, background: '#4a7c59', borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '14px' }}>
                    +
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => {
                      const file = e.target.files[0]; if (!file) return;
                      const fileExt = file.name.split('.').pop().toLowerCase();
                      const fileName = `${Date.now()}_profile_${(profileForm.business_name||'seller').replace(/[^a-zA-Z0-9]/g,'_')}.${fileExt}`;
                      const { error: upErr } = await supabase.storage.from('plant-images').upload(fileName, file, { contentType: file.type });
                      if (!upErr) { const { data: u } = supabase.storage.from('plant-images').getPublicUrl(fileName); setProfileForm(f => ({ ...f, profile_photo_url: u.publicUrl })); }
                    }} />
                  </label>
                </div>
              ) : (
                <label style={{ width: '100px', height: '100px', borderRadius: '50%', border: '2px dashed #4a7c59', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: '#f7f3ec' }}>
                  <span style={{ fontSize: '24px', marginBottom: '4px' }}>+</span>
                  <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '10px', color: '#4a7c59', fontWeight: 700, textAlign: 'center' }}>Upload</span>
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => {
                    const file = e.target.files[0]; if (!file) return;
                    const fileExt = file.name.split('.').pop().toLowerCase();
                    const fileName = `${Date.now()}_profile_${(profileForm.business_name||'seller').replace(/[^a-zA-Z0-9]/g,'_')}.${fileExt}`;
                    const { error: upErr } = await supabase.storage.from('plant-images').upload(fileName, file, { contentType: file.type });
                    if (!upErr) { const { data: u } = supabase.storage.from('plant-images').getPublicUrl(fileName); setProfileForm(f => ({ ...f, profile_photo_url: u.publicUrl })); }
                  }} />
                </label>
              )}
            </div>

            {/* Name, Tagline, Bio */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={labelStyle}>Business / Shop Name</label>
              <input value={profileForm.business_name} onChange={(e) => setProfileForm({ ...profileForm, business_name: e.target.value })} style={{ ...inp, marginBottom: '10px' }} />
              <label style={labelStyle}>Tagline</label>
              <input value={profileForm.tagline || ''} onChange={(e) => setProfileForm({ ...profileForm, tagline: e.target.value })} placeholder="e.g. Rare tropicals from Colorado" style={{ ...inp, marginBottom: '10px' }} />
              <label style={labelStyle}>Bio</label>
              <textarea value={profileForm.bio} onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })} style={{ ...inp, height: '72px', resize: 'vertical', marginBottom: '0' }} />
            </div>
          </div>

          {/* SOCIAL LINKS GRID */}
          <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(2, 1fr)', gap: '10px', marginBottom: '16px' }}>
            {[
              ['City', 'location_city', ''],
              ['State', 'location_state', ''],
              ['Etsy URL', 'etsy_url', 'https://etsy.com/shop/...'],
              ['Palmstreet URL', 'palmstreet_url', 'https://palmstreet.app/...'],
              ['eBay URL', 'ebay_url', 'https://ebay.com/...'],
              ['Facebook URL', 'facebook_url', 'https://facebook.com/...'],
              ['Instagram', 'instagram_url', '@yourhandle'],
              ['Website', 'website_url', 'https://yoursite.com'],
            ].map(([label, key, placeholder]) => (
              <div key={key}>
                <label style={labelStyle}>{label}</label>
                <input value={profileForm[key] || ''} onChange={(e) => setProfileForm({ ...profileForm, [key]: e.target.value })} placeholder={placeholder} style={{ ...inp, marginBottom: 0 }} />
              </div>
            ))}
          </div>

          {/* Storefront preview link */}
          {seller?.business_name && (
            <div style={{ background: '#f0f7f1', borderRadius: '12px', padding: '14px 16px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '12px', fontWeight: 700, color: '#4a7c59', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '1px' }}>Your Public Shop</div>
                <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#1e3d20' }}>trueplantvalue.com/shop/{slugify(seller?.business_name || '')}</div>
              </div>
              <button onClick={() => window.open(`/shop/${slugify(seller?.business_name || '')}`, '_blank')} style={{ background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)', color: '#f5f0e8', border: 'none', borderRadius: '10px', padding: '8px 18px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>
                View My Shop ↗
              </button>
            </div>
          )}

          <button onClick={handleSaveProfile} disabled={saving} style={{ background: saving ? '#8fb99a' : 'linear-gradient(135deg, #2c4a2e, #4a7c59)', color: '#f5f0e8', border: 'none', borderRadius: '12px', padding: '12px 28px', fontSize: '13px', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'Lato, sans-serif', width: mobile ? '100%' : 'auto' }}>
            {saving ? 'Saving...' : ' Save Profile'}
          </button>
          <div style={{ marginTop: '40px', paddingTop: '24px', borderTop: '1px solid rgba(143,185,154,0.3)' }}>
            <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '18px', color: '#c0392b', marginBottom: '8px' }}>Danger Zone</h3>
            <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#4a5e4b', marginBottom: '16px' }}>Deleting your account will permanently remove all your inventory data.</p>
            <button onClick={handleDeleteAccount} style={{ background: 'transparent', border: '2px solid #c0392b', color: '#c0392b', borderRadius: '12px', padding: '10px 24px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>Delete My Account</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellerDashboard;